import React, { useEffect, useRef, useState } from 'react';
import { Wallet, DollarSign, Users, TrendingUp, Loader2, Receipt, AlertCircle } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { getFunctionErrorMessage } from '../../src/lib/functionErrors';

interface BonusResumo {
    total: number;
    recebido: number;
}

interface Pagamento {
    id: string;
    valor: number;
    data_pagamento: string;
    tipo: 'venda_energia' | 'equipe';
}

interface Resumo {
    recebidoEsteMes: number;
    disponivel: number;
    bonusVendaEnergia: BonusResumo;
    bonusEquipe: BonusResumo | null;
    historico: Pagamento[];
}

const fmtMoney = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

const MeuDinheiroView: React.FC = () => {
    const [resumo, setResumo] = useState<Resumo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const historicoRef = useRef<HTMLDivElement>(null);

    const fetchResumo = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error } = await supabase.functions.invoke('get-minhas-comissoes');
            if (error) throw error;
            if (data?.success === false) throw new Error(data.error || 'Erro ao buscar resumo financeiro.');
            setResumo(data.data);
        } catch (err) {
            const message = await getFunctionErrorMessage(err, 'Erro ao buscar resumo financeiro.');
            console.error('Erro ao buscar resumo financeiro:', err);
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchResumo(); }, []);

    const verNoExtrato = () => historicoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-gray-400" /></div>;
    }
    if (error || !resumo) {
        return (
            <div className="flex flex-col items-center text-center py-16">
                <AlertCircle size={40} className="text-red-300 dark:text-red-800 mb-3" />
                <p className="text-sm font-bold text-red-700 dark:text-red-300 mb-1">Não foi possível carregar seus dados financeiros</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 max-w-sm">{error || 'Erro desconhecido.'}</p>
                <button
                    onClick={fetchResumo}
                    className="px-4 py-2 rounded-full text-sm font-bold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    Tentar novamente
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="w-full bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center text-white">
                        <Wallet size={22} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">Meu Dinheiro</h2>
                        <p className="text-sm text-white/80">Acompanhe seus bônus e pagamentos</p>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-3">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                            <DollarSign size={16} />
                        </div>
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Disponível para saque</p>
                    </div>
                    <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{fmtMoney(resumo.disponivel)}</p>
                    <button onClick={verNoExtrato} className="text-xs font-bold text-primary hover:underline mt-1">ver no extrato →</button>
                </div>

                <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between">
                    <div>
                        <p className="text-[11px] font-bold text-white/70 uppercase tracking-wide">Recebido este mês</p>
                        <p className="text-xl font-extrabold text-white">{fmtMoney(resumo.recebidoEsteMes)}</p>
                    </div>
                    <TrendingUp size={22} className="text-white/70" />
                </div>
            </div>

            {/* Bônus de Venda de Energia */}
            <div className="w-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                        <DollarSign size={18} />
                    </div>
                    <div>
                        <p className="font-bold text-gray-900 dark:text-white">Bônus de Venda de Energia</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Vendas diretas que você fechou</p>
                    </div>
                </div>
                <p className="text-2xl font-extrabold text-green-600 dark:text-green-400">{fmtMoney(resumo.bonusVendaEnergia.total)}</p>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">{fmtMoney(resumo.bonusVendaEnergia.recebido)} recebidos</p>
                <button onClick={verNoExtrato} className="text-xs font-bold text-green-600 dark:text-green-400 hover:underline mt-1">ver no extrato →</button>
            </div>

            {/* Bônus de Equipe — só parceiro, que tem rede */}
            {resumo.bonusEquipe && (
                <div className="w-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 shrink-0 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                            <Users size={18} />
                        </div>
                        <div>
                            <p className="font-bold text-gray-900 dark:text-white">Bônus de Equipe</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">Sobre as vendas da sua equipe (overline)</p>
                        </div>
                    </div>
                    <p className="text-2xl font-extrabold text-purple-600 dark:text-purple-400">{fmtMoney(resumo.bonusEquipe.total)}</p>
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">{fmtMoney(resumo.bonusEquipe.recebido)} recebidos</p>
                    <button onClick={verNoExtrato} className="text-xs font-bold text-purple-600 dark:text-purple-400 hover:underline mt-1">ver no extrato →</button>
                </div>
            )}

            {/* Histórico de Pagamentos */}
            <div ref={historicoRef} className="w-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 p-5 border-b border-gray-100 dark:border-gray-700">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <Receipt size={18} />
                    </div>
                    <div>
                        <p className="font-bold text-gray-900 dark:text-white">Histórico de Pagamentos</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Comissões já pagas</p>
                    </div>
                </div>

                {resumo.historico.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">Nenhum pagamento registrado ainda.</p>
                ) : (
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                        {resumo.historico.map(p => (
                            <div key={p.id} className="flex items-center justify-between px-5 py-3">
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        {p.tipo === 'venda_energia' ? 'Bônus de Venda de Energia' : 'Bônus de Equipe'}
                                    </p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500">{fmtData(p.data_pagamento)}</p>
                                </div>
                                <p className="text-sm font-bold text-green-600 dark:text-green-400">{fmtMoney(p.valor)}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MeuDinheiroView;